import {
    FormControl,
    FormControlProps,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectProps,
} from '@mui/material';
import React, { useRef, useEffect, useId } from 'react';
import { FieldConfigProps } from '.';
import { useField } from 'formik';

export interface FormSelectOption {
    value: number | string;
    name?: string;
    disabled?: boolean;
}

export type FormSelectProps = FieldConfigProps &
    Partial<Omit<SelectProps, 'onChange' | 'children'>> & {
        children?: React.ReactElement<{
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value: any;
            disabled?: boolean;
        }>[];
        // Supporting both children and options for now, for ease
        options?: FormSelectOption[];
        label?: string;
        size?: 'small' | 'medium';
        formControlProps?: FormControlProps;
        onChange?: (value: any) => void;
        helperText?: string;
        /**
         * Don't reset field value when it doesn't correspond to any available option
         */
        allowOrphaned?: boolean;
        shrinkLabel?: boolean;
    };

export const FormSelect: React.FunctionComponent<FormSelectProps> = ({
    name,
    validate,
    size,
    label,
    options,
    fullWidth = true,
    formControlProps,
    value,
    helperText,
    allowOrphaned,
    shrinkLabel,
    children,
    ...rest
}) => {
    const inputLabel = useRef<HTMLLabelElement>(null);
    const [field, { touched, error, initialValue }, { setValue }] = useField({ name, validate });
    const labelId = useId();

    useEffect(() => {
        if (!allowOrphaned && options) {
            const values = options?.filter((option) => !option.disabled).map((option) => option.value);
            if (!value && field.value && !values?.includes(field.value)) {
                setValue(values?.includes(initialValue) ? initialValue : '');
            }
        }
    }, [options, allowOrphaned, value, field.value, setValue, initialValue]);

    return (
        <FormControl fullWidth={fullWidth} variant="outlined" {...formControlProps} error={!!error}>
            <InputLabel ref={inputLabel} id={labelId} size="small" shrink={shrinkLabel}>
                {label}
            </InputLabel>

            <Select
                {...rest}
                {...field}
                labelId={labelId}
                size={size}
                label={label}
                onChange={(event) => {
                    setValue(event.target.value);
                    rest.onChange?.(event.target.value as string);
                }}
                value={value ?? field.value ?? ''}
            >
                {options
                    ? options.map((option) => {
                          return (
                              <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                                  {option.name}
                              </MenuItem>
                          );
                      })
                    : children}
            </Select>

            {helperText && <FormHelperText>{helperText}</FormHelperText>}
            {touched && !!error && <FormHelperText error>{error}</FormHelperText>}
        </FormControl>
    );
};

const renderOptionItem = (item: FormSelectOption) => {
    if (!(Object.prototype.hasOwnProperty.call(item, 'name') && Object.prototype.hasOwnProperty.call(item, 'value'))) {
        throw new Error('`Option` must have `name` and `value`');
    }

    return (
        <MenuItem key={item.value} value={item.value} disabled={item.disabled}>
            {item.name}
        </MenuItem>
    );
};

export const renderSelectOptions = (options: FormSelectOption[]) => options.map(renderOptionItem);
